// 2513 Croton Ave · interactive photoreal walkthrough. Entry point.
import * as THREE from 'three';
import { warmPrograms } from './shader_warmup.js';
import { createLighting } from './lighting.js';
import { buildHouse, getDoors, LOW_WALLS } from './house.js';
import { buildGarden } from './grass.js';
import { Player } from './player.js';
import { Layout } from './layout.js';
import { Interact } from './interact.js';
import { Minimap } from './minimap.js';
import { Viewpoints, OVERLAY_LAYER } from './viewpoints.js';
import { RedNote } from './rednote.js';
import { Tour } from './tour.js';
import { buildGrid } from './nav.js';
import { Markers } from './markers.js';
import { UI, t } from './ui.js';
import { Human } from './human.js';
import { batchStatic, stillParts, updateSwayLod, updateRoomVisibility } from './batch.js';
import { batchBaked, stowSources } from './batch_baked.js';
import { createPlantClusters } from './plant_clusters.js';
import { createRestClusters } from './rest_clusters.js';
import * as roles from './lighting_roles.js';
import { Pets } from './pets/roam.js';
import { setAnisotropy, setTextureCap, setTextureSet, texturesDecoded } from './materials.js';
import { TIERS, detectTier, detectTexSet, qualityPair } from './tier.js';
import { Stats } from './stats.js';
import { Demo } from './demo.js';
import { PhotoMode } from './photo.js';
import { PhotoUI } from './photo_ui.js';
import { applyLightmaps } from './lightmap.js';
import { createContact } from './contact.js';
import { bakedRequested, bakedCapabilityError } from './baked_mode.js';
import { createGuard } from './startup_guard.js';
import { createTAA } from './taa.js';
import { featureWanted } from './volumetric_slots.js';
import { createRoomCulling } from './visibility.js';
import { createFrameRate } from './frame_rate.js';
import { createStartQuality } from './start_quality.js';
import { advanceFrameClock } from './frame_clock.js';
import { slotSwitch } from './slot_dependents.js';
import { createLens } from './lens.js';
import { lensWanted, lensParts } from './lens_policy.js';
import { setLensShift } from './lens_shift.js';

// ---- Device tier first: it sets MSAA, pixel ratio, shadow map size, post passes and texture limits before any of them exist.
const Q = new URLSearchParams(location.search);
const detected = detectTier();
const preset = TIERS[detected.tier];
const texSet = detectTexSet(detected.tier);
setAnisotropy(Math.min(8, preset.anisotropy)); setTextureCap(preset.textureCap); setTextureSet(texSet);
// Render at most 1440p: the drawing buffer's short side stays within 1440 device pixels and its area within 2560 × 1440,
// so an ultrawide screen keeps its full height budget spread over the width. The canvas still covers the viewport and
// the browser upsamples the frame to the screen.
const MAX_SHORT = 1440, MAX_PIXELS = 2560 * 1440;
const pixelRatio = p => {
  const w = canvas.clientWidth || innerWidth, h = canvas.clientHeight || innerHeight;
  return Math.min(devicePixelRatio, p.pixelRatio, MAX_SHORT / Math.min(w, h), Math.sqrt(MAX_PIXELS / (w * h)));
};
const canvas = document.getElementById('gl');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: detected.tier === 'hq', powerPreference: 'high-performance', preserveDrawingBuffer: true });
// The context can be lost at any point of the load (startup_guard.js): non-essential steps fail soft and are retried after the
// restore, and the steps that need a live context wait for it. Everything below that touches the GPU is written for that.
const gl0 = renderer.getContext();
const guard = createGuard({ canvas, isLost: () => gl0.isContextLost() });
const soft = guard.soft;
await guard.waitForContext();   // the capability probe below reads extensions, which a lost context reports as missing
const bakedWanted = bakedRequested(location.search);
let bakedUnavailable = bakedWanted ? bakedCapabilityError(renderer) : null;
renderer.setPixelRatio(pixelRatio(preset));
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(62, innerWidth / innerHeight, 0.05, 300);
scene.add(camera);
// Size the drawing buffer from the canvas box (CSS keeps #gl covering the viewport; setSize(..., false) writes no inline size).
// Registered before any await so viewport changes during the loading screen still reach the canvas; composer joins once lighting exists.
let post = null, activePreset = preset, photoFit = null, repaint = null;
function fitView() {
  const w = canvas.clientWidth || innerWidth, h = canvas.clientHeight || innerHeight;
  const pr = pixelRatio(activePreset);
  if (pr !== renderer.getPixelRatio()) { renderer.setPixelRatio(pr); post?.setPixelRatio(pr); }
  camera.aspect = w / h; camera.updateProjectionMatrix();
  renderer.setSize(w, h, false); post?.setSize(w, h);
  photoFit?.();
  repaint?.();   // setSize clears the drawing buffer: draw again now, or the cleared canvas shows until the next frame
}
fitView();
// Resizes arrive in bursts while a window edge is dragged, and each fitView reallocates the drawing buffer and every
// post-processing target. Apply at most one every 500 ms, plus a trailing one when the burst ends, so the final size is
// always exact; in between the browser stretches the last frame (each applied fit repaints at once, fitView).
const FIT_EVERY_MS = 500;
let fitTimer = 0, fitLast = -Infinity;
function scheduleFit() {
  const wait = FIT_EVERY_MS - (performance.now() - fitLast);
  if (wait <= 0 && !fitTimer) { fitLast = performance.now(); fitView(); return; }
  if (!fitTimer) fitTimer = setTimeout(() => { fitTimer = 0; fitLast = performance.now(); fitView(); }, Math.max(wait, 0));
}
new ResizeObserver(scheduleFit).observe(canvas);
addEventListener('resize', scheduleFit);
visualViewport?.addEventListener('resize', scheduleFit);

const state = { dusk: Q.has('dusk'), tier: detected.tier, texSet, gpu: detected.gpu, tierSource: detected.source, quality: detected.tier, qualityHigh: qualityPair(detected.tier).find(q => q !== 'mobile') ?? 'mobile', ready: false, loaded: 0, total: 1, mode: 'walk', onLock: null };
const ui = new UI(state);
const house = await (await fetch('data/house.json')).json();
const points = await (await fetch('data/floorplan_points.json')).json();
const tour = await (await fetch('data/tour.json')).json();
const posts = await (await fetch('data/rednote_posts.json')).json();
// The precomputed daylight bake (see daylight.js); ?daylight=live ignores it, which tools/bake_daylight.mjs uses.
const daylightFile = Q.get('daylight') === 'live' ? Promise.resolve(null) : fetch('data/daylight.bin').then(r => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
const photoSpots = await fetch('data/photo_spots.json').then(r => r.ok ? r.json() : []).catch(() => []);

// ---- Lighting: daylight openings, practical fixtures and separate room / sky reflections.
await guard.waitForContext();
const lighting = createLighting(renderer, scene, camera, house, preset);
if (bakedWanted && !lighting.renderConfig.hdr) bakedUnavailable = 'HDR framebuffer unavailable';
const { fixtures, composer } = lighting;
post = composer; fitView();
// TAA (taa.js): ?taa[=internal scale] / ?taa=0, else the tier's flag (a number: the scale). It takes over the linear chain
// before any other pass joins it (contact, volumetric light, local adaptation find it as lighting.taa).
if (lighting.renderConfig.hdr && featureWanted(location.search, 'taa', preset)) soft('taa', () => { lighting.taa = createTAA(renderer, camera, lighting, Q.get('taa') || preset.taa); });
// The objects that follow the time of day (onTime: the fire pit, the sheer curtains); a slot switch starts them with its
// crossfade (slot_dependents.js)
const timeDependents = (dusk, instant) => { for (const o of layout.placed) soft('onTime', () => o.userData.onTime?.(dusk, instant)); };
async function applyTime() {
  if (baked) state.dusk = baked.slotName === 'dusk';
  await soft('lighting.applyTime', () => lighting.applyTime(baked ? false : state.dusk));   // baked slots own their sky, sun and physical lamp level
  timeDependents(state.dusk, !state.ready);
  soft('pets relight', () => pets?.relight());
  if (baked) { try { await baked.warmDepth(); baked.afterTime(); } catch (err) { if (gl0.isContextLost()) soft('baked.afterTime', async () => { await baked?.warmDepth(); baked?.afterTime(); }); else await fallbackLegacy(err); } }
  ui.syncLegends();
}

// ---- Build ----
// Each step yields a painted frame so the bar and its percentage actually move.
const step = (p, msg) => { ui.progress(p, msg); return new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0))); };
// Long awaits (texture downloads, shader compile) keep the percentage moving: it eases from `from` toward `to`, never past
// the real fraction done when one is reported, and lands on `to` once the work settles.
async function during(from, to, msg, work) {
  let shown = from, real = 0;
  ui.progress(from, msg);
  const timer = setInterval(() => { shown += (to - shown) * 0.04; ui.progress(Math.max(shown, from + (to - from) * real), msg); }, 250);
  try { return await work((f) => { real = Math.min(1, f); }); } finally { clearInterval(timer); ui.progress(to, msg); }
}
ui.progress(0.05, 'sky');
// Only the sky the scene opens in; the other one is convolved after the bar reaches 100%.
await soft('sky', () => lighting.loadSky(state.dusk));   // not fatal: applyTime asks for it again
ui.progress(0.15, 'house');
const houseRoot = buildHouse(house);
scene.add(houseRoot);
ui.progress(0.25, 'garden');
const garden = buildGarden({ quality: preset.grass, camera });
scene.add(garden.group);
ui.progress(0.3, 'furniture');
const layout = new Layout(scene, fixtures, house, points);
await layout.build((p, msg) => ui.progress(0.3 + p * 0.35, msg));
await step(0.66, 'materials');
// The lighting manifest (lighting_roles.js): every mesh and material resolved before the interior / exterior material
// split reads it; a second pass after batching resolves the switches' plates, the lamp-surface copies and merged meshes.
roles.classifyLighting(scene, { stillParts });
lighting.collectMaterials(scene);
await step(0.68, 'finishing');
const batch = Q.get('batch') === '0' ? { lod: [] } : batchStatic([houseRoot, layout.root], { skip: getDoors().map(d => d.group), anchors: layout.interactive, animated: layout.animated });
roles.classifyLighting(scene);
// Baked sunrise is the default. Keep the complete legacy setup until staging succeeds.
let baked = null, batch2 = null, plantClusters = null, restClusters = null, stowed = null;
state.baked = false;
state.bakedFallback = bakedUnavailable;
if (bakedWanted && !state.bakedFallback) {
  await step(0.69, 'lighting');
  try {
    baked = await applyLightmaps({ scene, renderer, lighting, movables: layout.movables,
      dynamic: [...getDoors().map(d => d.group), ...layout.animated.filter(o => o.userData.toggle)],
      slot: Q.get('lightmap') || 'sunrise', tier: Q.has('fast') ? 'mobile' : state.quality,
      onProgress: p => ui.progress(0.69 + p * 0.22) });
    state.baked = true; state.dusk = false;
    state.bakedSlots = baked.availableSlots; state.bakedSlot = baked.slotName;
    // what batchStatic had to keep apart for the bake, merged now that it is applied (batch_baked.js); ?batch2=0: off
    if (Q.get('batch2') !== '0') {
      try {   // a failure leaves the scene as it was: the walkthrough draws the meshes unmerged
        const plants = layout.animated.filter(o => !o.userData.toggle && roles.roleOf(o) === 'plant');
        batch2 = batchBaked({ roots: [houseRoot, layout.root], skip: [...getDoors().map(d => d.group), ...layout.movables, ...plants], moving: layout.animated.filter(o => !plants.includes(o)) });
        roles.classifyLighting(scene); baked.sunShadow.invalidate();
        state.batch2 = batch2.stats;
      } catch (err) { console.warn('runtime merges skipped', err); }
    }
    // the far plants' frozen copies per material and cell, for the walkthrough camera (plant_clusters.js); ?plantmerge=0: off
    if (Q.get('plantmerge') !== '0' && batch.world) {
      try { plantClusters = createPlantClusters({ lod: batch.lod, camera, parent: batch.world }); state.plantClusters = plantClusters.stats; }
      catch (err) { console.warn('far plant merge skipped', err); plantClusters = null; }
    }
    // doors, toggles and carried pieces at rest, per material and cell, for the walkthrough camera (rest_clusters.js);
    // ?restmerge=0: off
    if (Q.get('restmerge') !== '0' && batch.world) {
      const toggles = new Set([...layout.animated, ...layout.interactive].filter(o => o.userData.toggle));
      try { restClusters = createRestClusters({ roots: [...getDoors().map(d => d.group), ...layout.movables, ...toggles], camera, parent: batch.world }); state.restClusters = restClusters.stats; }
      catch (err) { console.warn('rest merge skipped', err); restClusters = null; }
    }
    // the merged meshes' hidden sources, per parent in one hidden group: the render walk skips them (?stow=0: off)
    if (Q.get('stow') !== '0') {
      try { stowed = stowSources([houseRoot, layout.root]); state.stowed = { groups: stowed.groups, sources: stowed.sources }; }
      catch (err) { console.warn('stowing skipped', err); stowed = null; }
    }
    // the enclosed fit-outs updateRoomVisibility shows and hides: tracked casters for the sun-shadow cache, so walking in
    // and out of their rooms redraws base around them, not all of it (a 60 ms hitch at bedroom 3's door)
    if (batch.rooms?.length) baked.sunShadow.track(batch.rooms.flatMap(r => [r.group, ...r.roots]), false, true);   // rigid: only shown and hidden
  } catch (err) { state.bakedFallback = err.message; }
}
if (lighting.taa && baked) lighting.taa.noise = baked.sunShadow.uniforms.uSunFrame;   // PCSS rotations integrate under TAA
await step(0.92, 'controls');

// ---- Player, interaction, UI systems ----
const player = new Player(camera, canvas, state);
player.teleport(+(Q.get('x') ?? 0.0), -(+(Q.get('y') ?? -6.9)), +(Q.get('h') ?? 0), +(Q.get('p') ?? 0));   // default: the front walk, looking at the house
const human = new Human(scene, player); baked?.register(human.root);
const interact = new Interact(scene, camera, player, layout, ui, human);
const pets = Q.get('pets') === '0' ? null : new Pets({ scene, player, layout, camera, ui, lighting, seed: Q.has('petseed') ? +Q.get('petseed') : undefined });
if (pets) for (const a of pets.agents) baked?.register(a.root);
interact.pets = pets;
function applyFeatures(p) {
  if (!baked) return;
  if (p.contact && !scene.userData.contact) soft('contact shadows', () => createContact({ scene, camera, lighting, movables: layout.movables, pets, player, human }));
  soft('contact shadows', () => scene.userData.contact?.setEnabled(p.contact));
  soft('baked features', () => baked.setFeatures(p.name));
}
applyFeatures(Q.has('fast') ? TIERS.mobile : preset);
const minimap = new Minimap(player, layout, points, tour, posts, ui, (target) => goTo(target));
const viewpoints = new Viewpoints(scene, camera, player, points, tour, ui, renderer, photoSpots);
const rednote = new RedNote(posts, ui, viewpoints);
const tourCtl = new Tour(player, tour, points, ui, viewpoints);
const markers = new Markers(scene, camera, player, rednote, viewpoints, ui, houseRoot);
interact.markers = markers;
interact.viewRoots = viewpoints.viewRoots = [houseRoot, layout.root];
viewpoints.markers = markers;
if (Q.get('pins') === '0') Object.assign(markers.enabled, { rn: false, pano: false, photo: false });
minimap.setPlan(house); minimap.markers = markers; minimap.targets = viewpoints.targets;
minimap.setExtra(rednote.blips());
function goTo(target) { const pin = markers.pins.find(p => p.kind === target.kind && p.target.id === target.id); if (pin) markers.activate({ pin }); else viewpoints.goTo(target); }
canvas.addEventListener('webglcontextrestored', () => soft('pets relight', () => pets?.relight()));   // after lighting rebuilt its environments (its own listener ran first)
// ---- The cinematic lens (lens.js): depth of field with autofocus, bloom, vignetting, lateral chromatic aberration, after
// TAA's resolve. On by default where the tier says (hq); Y and the legend row toggle it and the choice is remembered;
// ?lens=0 / ?lens=1 force it, ?dof=0 / ?bloom=0 drop a part, ?capture leaves it off (lens_policy.js lensWanted).
const lensStored = () => { try { return localStorage.getItem('croton-lens'); } catch { return null; } };
const lensForward = new THREE.Vector3();
// autofocus on what the visitor aims at (a piece, a door, a pet; not a pin or a photo card), as a distance along the view axis
const lensAim = () => {
  const h = interact.aimed; if (!h || h.pin || h.card || !(h.dist > 0)) return null;
  camera.getWorldDirection(lensForward);
  return { distance: h.dist * Math.max(0.1, lensForward.dot(interact.aimDir)) };
};
const lens = lighting.renderConfig.hdr && Q.get('lens') !== '0' ? soft('lens', () => createLens({ lighting, renderer, camera, aim: lensAim, parts: lensParts(location.search) })) : null;
state.lensAvailable = !!lens;
function applyLens(p = activePreset) {
  if (!lens) { state.lens = false; return; }
  lens.enabled = state.lens = lensWanted(location.search, p, lensStored());
}
function toggleLens() {
  if (!lens) return;
  try { localStorage.setItem('croton-lens', lens.enabled ? '0' : '1'); } catch { /* private mode: this visit only */ }
  lens.enabled = state.lens = !lens.enabled; ui.syncLegends();
}
applyLens(preset);
ui.bind({ player, viewpoints, rednote, markers, tour: tourCtl, minimap, pets, toggleDusk, toggleQuality, toggleLens });

// ---- Post-processing: GTAO (contact shadows/ambient occlusion) + SMAA, per tier preset. J toggles quality.
// Toggling dusk needs the other sky now, so it opens the background preparation's quiet-moment gate (see below).
let skyUrgent = false;
let slotRequestId = 0;
// the bake's slot switch, its dependents started with the crossfade and settled on the slot shown (slot_dependents.js)
const switchSlot = slotSwitch({ setSlot: (name, progress) => baked.setSlot(name, progress), apply: slot => timeDependents(slot === 'dusk', false), shown: () => baked?.slotName ?? null });
async function setBakedSlot(name, { quiet = false } = {}) {   // quiet: the tour names the time in its own caption
  if (!state.ready || !firstFrameDrawn || state.bakedSlotPending || !baked || !baked.availableSlots.includes(name)) return false;
  const requestId = ++slotRequestId;
  state.bakedSlotPending = name; ui.syncLegends();
  const loading = quiet ? { progress() {}, hide() {} } : ui.slotLoading(name);   // the tour switches without the loading screen
  try {
    const switched = await switchSlot(name, loading.progress);
    if (!switched || requestId !== slotRequestId || !baked) return false;
    state.bakedSlot = baked.slotName; state.dusk = baked.slotName === 'dusk';
    ui.syncLegends(); if (!quiet) ui.toast(t(`slot_${baked.slotName}`));
    return true;
  } catch (err) { console.warn('baked slot kept previous scene', err); loading.hide(); if (requestId === slotRequestId) ui.toast(t('slotFailed')); return false; }
  finally { loading.hide(); state.bakedSlotPending = null; ui.syncLegends(); }
}
function toggleDusk() {
  if (baked) {
    if (baked.availableSlots.length < 2) { ui.toast(t('sunriseOnly')); return; }
    return setBakedSlot(baked.nextSlot());
  }
  skyUrgent = true; state.dusk = !state.dusk; ui.syncLegends(); applyTime();
}
// Mirror reflections exist only in the hq preset; their proxy scenes are built the first time it is used.
// Foliage antialiasing. Leaves are alpha-tested cut-outs, which MSAA cannot smooth on its own; alpha-to-coverage lets the
// cut-out edge use the multisample coverage instead. Mipmaps average a leaf's alpha down at distance, which thins
// canopies under alpha-to-coverage, so the cutoff drops to 45% of its original while it is on. Without MSAA,
// alpha-to-coverage would draw whole leaf cards, so it follows the tier's sample count.
const foliageMaterials = new Set();
function collectFoliageMaterials() {
  foliageMaterials.clear();
  scene.traverse(o => { for (const m of [].concat(o.material ?? [])) if (m.alphaTest > 0 && !m.transparent) foliageMaterials.add(m); });
}
collectFoliageMaterials();
function setFoliageAA(on) {
  for (const m of foliageMaterials) {
    m.userData.cutoff ??= m.alphaTest;
    const cutoff = on || lighting.taa ? m.userData.cutoff * 0.45 : m.userData.cutoff;   // PCSS depth copies keep dense leaf shadows under TAA too
    if (m.alphaToCoverage !== on || m.alphaTest !== cutoff) { m.alphaToCoverage = on; m.alphaTest = cutoff; m.needsUpdate = true; }
  }
}
setFoliageAA(!lighting.taa && lighting.renderConfig.samples > 0);
let mirrorsReady = false;
const mirrorsOn = () => lighting.renderConfig.hdr && TIERS[state.quality].mirrors;
function setQuality(name) {
  const p = TIERS[name]; if (!p) return Promise.resolve(); state.quality = name;
  garden.setQuality(p.grass); lighting.setQuality(p);
  applyFeatures(p);
  const switching = baked?.setQuality(name).catch(fallbackLegacy);
  activePreset = p; setFoliageAA(!lighting.taa && lighting.renderConfig.samples > 0); fitView();
  applyLens(p);
  if (mirrorsOn() && !mirrorsReady && state.ready) { layout.prepareMirrors(); mirrorsReady = true; }
  ui.syncLegends();
  return switching ?? Promise.resolve();
}
async function fallbackLegacy(err) {
  if (!baked) return;
  state.bakedFallback = err.message;
  stowed?.undo(); stowed = null;
  batch2?.undo(); batch2 = null;   // its merged meshes carry the baked materials
  plantClusters?.dispose(); plantClusters = null; restClusters?.dispose(); restClusters = null;
  baked.dispose(); baked = null; state.baked = false;
  if (window.__app) { window.__app.baked = null; window.__app.batch2 = null; }
  ui.renderHelp(); ui.syncLegends();
  const file = await daylightFile;
  if (!lighting.daylight?.load(file)) lighting.bakeDaylight();
  lighting.setQuality(TIERS[state.quality]);
  collectFoliageMaterials(); setFoliageAA(lighting.renderConfig.samples > 0);
  await applyTime();
  if (mirrorsReady) layout.prepareMirrors();
}
// The detected tier is the starting point; J switches to its pair (hq/laptop <-> mobile, mobile <-> laptop) and back.
// (J also ends the adaptive start: the visitor's choice stands)
let startQuality = null;
function toggleQuality() { startQuality?.cancel(); const [own, other] = qualityPair(state.tier); setQuality(state.quality === own ? other : own); }
ui.renderHelp(); ui.syncLegends();

if (Q.has('eye')) { player.eyeHeight = +Q.get('eye'); player.eye = player.eyeHeight; }
if (Q.has('fast')) setQuality('mobile');
if (mirrorsOn()) { layout.prepareMirrors(); mirrorsReady = true; }
await step(0.93, 'textures');
await during(0.93, 0.955, 'textures', texturesDecoded);
// One time of day under the loading screen. Enter turns on before the warm draw: a Windows shader compile must not leave the button disabled.
await step(0.955, 'lighting');
const warmupStart = performance.now(), warmup = state.warmupSteps = {};
const lap = (k, t) => { warmup[k] = Math.round(performance.now() - t); return performance.now(); };
let lapStart = warmupStart;
await applyTime(); lapStart = lap('time', lapStart);
await step(0.96, 'shaders'); lapStart = lap('yield1', lapStart);
// Enter turns on before the shaders exist, so a slow compile never leaves the button disabled. Entering before the
// first frame is drawn shows a loading screen over the empty canvas until the warm draw below has put a frame on it.
const warming = document.getElementById('warming');
let firstFrameDrawn = false;
document.getElementById('enter').addEventListener('click', () => { state.enteredAt ??= performance.now(); if (!firstFrameDrawn) ui.showLoading(); });
state.ready = true; state.loadMs = performance.now(); ui.ready(); fitView();
await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
lapStart = lap('yield2', lapStart);
// Link every program before the first draw. With KHR_parallel_shader_compile (ANGLE exposes it on D3D11, Vulkan and
// Metal) the GPU process compiles on all cores while this polls; drawing first instead links and waits for the ~100
// programs one at a time on a single core, which on Windows blocks the tab for minutes (measured on an RTX 3070
// laptop: 89 s blocked and Ready at 124 s on Vulkan, D3D11 never finished in 7 min; with this, 11 s and 46 s).
// The race guards drivers whose completion poll never flips: after the timeout the warm draw finishes the compile.
// Compile with the composer's buffer bound: three bakes "render target or screen" into each program (tone mapping,
// output colour space), so compiling for the screen here would compile every material twice.
const warmScene = () => warmPrograms(renderer, scene, camera, lighting.renderConfig.direct ? null : composer.readBuffer);
await guard.waitForContext();   // compiling on a lost context links nothing and may never answer
const compiling = warmScene().then(() => baked?.checkPatches()).catch(async err => {
  if (gl0.isContextLost()) { await guard.waitForContext(); return warmScene(); }   // not the bake's fault: compile again once restored
  if (!baked) throw err;
  await fallbackLegacy(err); await warmScene();
}).catch(err => console.warn('shader warmup', err));   // never reject: the load awaits this below
const puddleShaders = soft('puddle shaders', () => baked?.puddleRefl?.warm?.());   // the ground reflection's own copies (puddles.js)
// Real progress for the loading screen: KHR_parallel_shader_compile answers "is this program linked yet?" without
// blocking, so the bar counts finished programs out of all of them. Without the extension the bar stays indeterminate.
const stopCompileProgress = (() => {
  const gl = renderer.getContext(), ext = gl.getExtension('KHR_parallel_shader_compile'), programs = renderer.info.programs || [];
  const bar = document.getElementById('warmbar'), count = document.getElementById('warmcount');
  if (!ext || !programs.length || !bar) return () => {};
  const tick = () => {
    let done = 0; for (const p of programs) if (gl.getProgramParameter(p.program, ext.COMPLETION_STATUS_KHR)) done++;
    warming.dataset.known = ''; bar.style.width = `${Math.round(4 + 88 * done / programs.length)}%`;
    count.textContent = `${t('warmCount')} ${done} / ${programs.length}`;
  };
  tick(); const id = setInterval(tick, 120); return () => { clearInterval(id); tick(); };
})();
// compileAsync has queued every link, so the GPU process is busy on all cores: the daylight bake (~2 s of CPU ray
// tests) runs now, overlapping the compile instead of adding to the load. Its textures upload before the warm draw.
// Use the shipped bake when it matches this build's walls, furniture and windows; otherwise run the ray tests here.
if (!baked) { // The file was requested at startup and is normally long here; never let a stalled request hold the load.
  const file = await Promise.race([daylightFile, new Promise(resolve => setTimeout(() => resolve(null), 4000))]);
  if (!lighting.daylight?.load(file)) {
    lighting.bakeDaylight();
  }
  state.daylightSource = lighting.daylight?.source; }
lapStart = lap('bake', lapStart);
await Promise.race([Promise.all([compiling, puddleShaders]), new Promise(resolve => setTimeout(resolve, 120000))]);
stopCompileProgress(); document.getElementById('warmbar').style.width = '94%';
// The first draw below blocks the page for a moment (texture uploads, shadow maps, the last shader links). Name that step and
// let it paint first, so a visitor already on the loading screen sees "Lighting the rooms" rather than a stalled count.
if (!warming.classList.contains('hidden')) {
  document.getElementById('warmcount').textContent = t('warmDraw');
  await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
}
lapStart = lap('compile', lapStart);
await guard.waitForContext();
try {
  layout.updateMirrors(camera, mirrorsOn(), renderer);
  composer.render();
  if (mirrorsOn()) layout.warmMirrors(renderer);
} catch (err) {
  console.warn('warmup', err);
  try { renderer.render(scene, camera); } catch (_) {}
}
lap('draw', lapStart);
// Room visibility (visibility.js): from here on the walkthrough camera skips what the plan's walls hide from it; shadows,
// mirrors and reflections still see everything. ?cull=0 turns it off.
const culling = Q.get('cull') === '0' ? null : soft('room culling', () => createRoomCulling({ THREE, camera, roots: [houseRoot, layout.root, garden.group, batch.world],
  moving: layout.interactive.filter(o => !o.userData.toggle), swinging: [...getDoors().map(d => d.group), ...layout.animated], doors: getDoors(), lowWalls: LOW_WALLS }));
firstFrameDrawn = true;
document.getElementById('warmbar').style.width = '100%';
ui.hideLoading();
state.lightingWarmupMs = performance.now() - warmupStart;
// The other time of day (HDR download, CPU tint, PMREM and room bounce) waits until the walkthrough is up.
// Both skies share the CubeUV environment layout and fixed light counts, so a dusk toggle reuses the compiled programs.
// Its CPU and GPU steps cost a ~150 ms hitch, so they run while the visitor is still on the landing page, or, once they
// have entered, no sooner than 8 s after entering and only while they stand still: never on the reveal, never mid-walk.
const idle = cb => (window.requestIdleCallback ? requestIdleCallback(cb, { timeout: 4000 }) : setTimeout(cb, 1500));
const titleShown = () => !document.getElementById('title').classList.contains('hidden');
const quietMoment = () => titleShown() || (performance.now() - (state.enteredAt ?? 0) > 8000 && player.keys.size === 0 && !tourCtl.active && !viewpoints.fly);
// The download starts right away; each heavy step waits for whenQuiet() (or for a dusk toggle, which needs it now).
const whenQuiet = () => new Promise(resolve => { const check = () => (skyUrgent || quietMoment() ? idle(resolve) : setTimeout(check, 500)); check(); });
if (!baked) idle(() => { const other = !state.dusk, t0 = performance.now(); soft('other sky', () => lighting.prepareTime(other, whenQuiet).then(() => { state.otherSkyMs = performance.now() - t0; })); });
if (Q.has('x') || Q.has('auto')) { document.getElementById('title').classList.add('hidden'); fitView(); }
document.getElementById('enter').addEventListener('click', fitView);

// ---- Loop. Deterministic hooks for screenshots: window.renderAt(t) draws exactly time t.
const clock = new THREE.Clock(); let simT = 0;
// Animated toggles (the garage door's ~3 s run, gates, lids) move shadow casters too: the sun shadow is refreshed while one
// moves or snaps to a new value, on every tier; lighting.update adds the frame after it settles.
const toggled = layout.animated.filter(o => o.userData.toggle).map(o => ({ toggle: o.userData.toggle, value: o.userData.toggle.value }));
const togglesMoving = () => toggled.reduce((any, t) => { const v = t.toggle.value, changed = t.toggle.moving || v !== t.value; t.value = v; return any || changed; }, false);
repaint = () => { if (!document.hidden) frame(0); };
// dt: the frame's step of the simulation clock (at most 0.1 s); gap: the time since the previous frame before that clamp
// (frame_clock.js)
function frame(dt, gap = dt) {
  if (photo.active) return;   // photo mode owns the canvas while it shows a still
  stats.begin();
  advanceFrameClock(dt, gap);
  simT += dt;
  tourCtl.update(dt);
  demo.update(dt);
  viewpoints.update(dt);
  player.update(dt);
  human.update(dt);
  // Non-essential per-frame steps fail soft (logged once), so one of them cannot stop the draw or the ready count below.
  soft('pets.update', () => pets?.update(dt));
  interact.update(dt);
  markers.update(dt);
  updateSwayLod(batch.lod, camera.position);
  updateRoomVisibility(batch.rooms, camera.position);
  plantClusters?.update();   // after both: which far plants show their frozen copy
  layout.animate(simT); if (!viewpoints.panoOpaque) soft('mirrors', () => layout.updateMirrors(camera, mirrorsOn(), renderer));
  garden.update(simT);
  minimap.draw();
  soft('lighting.update', () => lighting.update(dt, false, togglesMoving() || !!interact.grab || interact.returning.length > 0 || getDoors().some(d => d._t < 1)));
  try { baked?.updateMovables(dt); } catch (err) { if (!gl0.isContextLost()) fallbackLegacy(err); }   // a lost context is not the bake's fault
  restClusters?.update();   // after the sun-shadow cache's scan brought the tracked roots' matrices up to date
  if (culling) soft('room culling', culling.update);   // after everything that moves the camera or a door this frame
  try {
    // An opaque panorama hides the whole house, so only its sphere is drawn. While it fades in, the house goes through the
    // composer as usual and the sphere is laid over it: drawing the house straight to the screen instead would compile a
    // second, screen-target variant of every material on the spot (a 0.1-0.4 s freeze on entering a panorama).
    if (!viewpoints.panoOpaque) { if (state.composerFailed) renderer.render(scene, camera); else composer.render(dt); }   // dt: the composer's own clock is the wall clock
    // Overlay pass: the panorama sphere and the floating photo cards, on top of the finished frame.
    if (viewpoints.panoActive || markers.overlayActive) {
      // The scene's sky background would otherwise be drawn again over the finished frame, so it is lifted for this pass.
      const mask = camera.layers.mask, autoClear = renderer.autoClear, background = scene.background;
      camera.layers.set(OVERLAY_LAYER); renderer.autoClear = viewpoints.panoOpaque; scene.background = null;
      renderer.render(scene, camera);
      camera.layers.mask = mask; renderer.autoClear = autoClear; scene.background = background;
    }
  } catch (err) {
    if (!state.composerFailed) { state.composerFailed = true; console.warn('composer', err); }
    try { renderer.render(scene, camera); } catch (_) {}
  }
  stats.end();
}
// The demonstration (X): a scripted promenade through the controls.
// Photo mode: a path-traced still of the current view (see photo.js).
const photo = new PhotoMode({ renderer, scene, camera, lighting, getTier: () => state.tier });
const photoUI = new PhotoUI({ photo, player, ui });
photoFit = () => photo.onResize?.();
const demo = new Demo({ player, viewpoints, markers, pets, ui, tour: tourCtl, interact, layout });
document.getElementById('btn-demo').onclick = () => demo.toggle();
// Stats for nerds (hidden): the right-click menu's last item, the ` key, or ?stats=1.
const stats = new Stats({ renderer, camera, player, state, lighting, pets, canvas });
addEventListener('crotonstats', () => stats.toggle());
addEventListener('keydown', (e) => { if (e.code === 'Backquote' && !e.repeat && e.target?.tagName !== 'INPUT') stats.toggle(); });
if (Q.has('stats')) stats.toggle(true);
window.simTime = () => simT;   // read-only clock for deterministic tools: step with renderAt(simTime() + dt)
window.renderAt = (t) => { const step = Math.max(0, t - simT), dt = Math.min(0.1, step); frame(dt || 1 / 60, step); };
window.__app = { baked, batch2, photo, photoUI, demo, stats, tier: state.tier, scene, camera, player, layout, garden, viewpoints, tourCtl, rednote, state, applyTime, setQuality, toggleQuality, toggleDusk, toggleLens, lens, setLensShift: (y, x) => setLensShift(camera, y, x), setBakedSlot, renderer, lighting, goTo, minimap, interact, THREE, nav: { buildGrid }, getDoors, markers, pets, roles, culling };
// Frame pacing (frame_rate.js): the display's refresh rate when frames fit, else an even fraction of it (60, 40, 30 ... on a
// 120 Hz screen), stepping down rather than stuttering. ?fps=N caps at N, ?fps=0 draws every animation frame; phones keep
// the old 30 fps cap. Skipped display refreshes cost nothing.
const fpsParam = Q.get('fps'), fpsCap = fpsParam !== null && fpsParam !== '' && Number.isFinite(+fpsParam) ? Math.max(0, +fpsParam) : detected.tier === 'mobile' ? 30 : null;
const pacing = createFrameRate({ cap: fpsCap });
state.fpsCap = fpsCap; state.pacing = pacing;
let readyFrames = 0;
// ?capture: the app stops drawing on its own so a recorder can step it frame by frame with renderAt (tools/promo.mjs).
const CAPTURE = Q.has('capture');
// ?simdt=<s>: the page's own frames advance the clock by this, not by the time that passed (two pages stepped alike draw
// alike: tools/ab_check.mjs)
const SIM_DT = Q.has('simdt') ? Math.min(0.1, Math.max(0, +Q.get('simdt') || 0)) : null;
// Adaptive start (start_quality.js): a desktop's tier, guessed from its GPU's name, is timed once after the first frames
// and stepped up or down once (a step up is timed again and may be handed back). J, ?tier=, ?capture, ?adapt=0 and ?fast
// end it.
startQuality = createStartQuality({ tier: state.quality, enabled: detected.adaptive && !CAPTURE && Q.get('adapt') !== '0' && !Q.has('fast'),
  setTier: async name => {
    state.tier = name; state.tierSource = 'measured'; state.qualityHigh = qualityPair(name).find(q => q !== 'mobile') ?? 'mobile';
    await setQuality(name);
    await soft('puddle shaders', () => baked?.puddleRefl?.warm?.());   // hq's ground reflection, made just now
  } });
state.startQuality = startQuality.state;
const loop = (now) => {
  requestAnimationFrame(loop);
  if (CAPTURE && readyFrames >= 12) return;
  // The pacing is lifted inside an opaque panorama: drawing one textured sphere is nearly free, and panning a photo judders.
  if (!pacing.tick(now) && !viewpoints.panoOpaque) return;
  startQuality.begin(now);
  if (document.hidden || viewpoints.panoActive || photo.active) startQuality.pause(now);
  const timed = startQuality.wants(now);
  const elapsed = clock.getDelta(), dt = SIM_DT ?? Math.min(0.1, elapsed), gap = SIM_DT ?? elapsed, t0 = performance.now();
  if (!document.hidden) soft('frame', () => frame(dt, gap));
  const work = performance.now() - t0;
  if (timed) { gl0.finish(); startQuality.sample(work, performance.now() - t0, now); }   // the frame's whole cost, GPU included
  if (!viewpoints.panoOpaque) pacing.drawn(now, work);   // a panorama's cheap frames say nothing about the house's
  if (++readyFrames === 12) window.__ready = true;
};
requestAnimationFrame(loop);
